import { useEffect } from 'react'

import { connect, useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import { compose } from 'recompose'

import AnalyticsPostCampaignReportPost from '@components/Analytics/PostCampaignReport/Post/Post'
import { AnalyticsPostCampaignReportReel } from '@components/Analytics/PostCampaignReport/Reel/Reel'
import AnalyticsPostCampaignReportStory from '@components/Analytics/PostCampaignReport/Story/Story'
import AnalyticsPostCampaignReportSummary from '@components/Analytics/PostCampaignReport/Summary/Summary'
import AnalyticsPostCampaignReportTikTok from '@components/Analytics/PostCampaignReport/TikTok/TikTok'
import { PostCampaignReportChannelSummary } from '@components/Reports/PostCampaignReport/Channel/Summary/Summary'
import Loading from '@components/UI/Loading'
import { PCRChannels } from '@enums'
import { IApplicationState } from '@store'
import { selectIsCampaignFetched } from '@store/campaigns'
import {
  fetchCampaignMetrics,
  fetchPostMetrics,
  fetchReelMetrics,
  fetchSnapMetrics,
  fetchStoryMetrics,
  fetchTikTokMetrics,
  selectIsCampaignMetricsInErrorState,
  selectIsSummaryActive,
  selectIsSummaryFetched,
  selectShouldFetchPostMetrics,
  selectShouldFetchReelMetrics,
  selectShouldFetchStoryMetrics,
  selectShouldFetchTikTokMetrics,
  selectShowLoading,
} from '@store/postCampaignReport'
import { fetchPcrMetrics } from '@store/postCampaignReport/metrics/action'
import { fetchXMetrics } from '@store/postCampaignReport/xMetrics/actions'
import { selectActivePCRChannel, selectCampaignId } from '@store/router'
import PostCampaignReportPerformanceList from '../List'

const PCR_CHANNEL_SUMMARY_SUPPORTED_CHANNELS = [
  PCRChannels.X,
  PCRChannels.Snap,
  PCRChannels.YouTubeShorts,
  PCRChannels.YouTubeVideos,
]

const PostCampaignReportChannelSummaryWrapper: React.FC<{
  activeChannel: PCRChannels
  campaignId: number
}> = ({ activeChannel, campaignId }) => {
  const dispatch = useDispatch()
  const { isCampaignFetched, isSummaryFetched } = useSelector((state: IApplicationState) => {
    return {
      isCampaignFetched: selectIsCampaignFetched(state, campaignId),
      isSummaryFetched: selectIsSummaryFetched(state, campaignId),
    }
  })

  useEffect(() => {
    if (isCampaignFetched && isSummaryFetched) {
      if (activeChannel === PCRChannels.X) {
        dispatch(fetchXMetrics(campaignId))
      } else if (activeChannel === PCRChannels.Snap) {
        dispatch(fetchSnapMetrics(campaignId))
      } else if (
        activeChannel === PCRChannels.YouTubeShorts ||
        activeChannel === PCRChannels.YouTubeVideos
      ) {
        dispatch(fetchPcrMetrics(campaignId, activeChannel))
      }
    }
  }, [activeChannel, isCampaignFetched, isSummaryFetched])

  return (
    <PostCampaignReportChannelSummary
      channel={activeChannel}
      translationPrefix={`postCampaignReport.summary.${activeChannel}`}
    />
  )
}

export interface IInternalProps {
  activeChannel: PCRChannels
  campaignId: number
  isCampaignFetched?: boolean
  isSummaryFetched?: boolean
  showLoading?: boolean
  isCampaignMetricsInErrorState?: boolean
  shouldFetchPostMetrics?: boolean
  shouldFetchVideoMetrics?: boolean
  shouldFetchStoryMetrics?: boolean
  shouldFetchTikTokMetrics?: boolean
  shouldFetchReelMetrics?: boolean
  isSummaryActive?: boolean
  fetchCampaignMetrics: typeof fetchCampaignMetrics
  fetchPostMetrics: typeof fetchPostMetrics
  fetchStoryMetrics: typeof fetchStoryMetrics
}

const PostCampaignReportPerformanceListWrapper: React.FC<IInternalProps> = ({
  activeChannel,
  campaignId,
  isCampaignFetched,
  showLoading,
  isCampaignMetricsInErrorState,
  shouldFetchPostMetrics,
  shouldFetchStoryMetrics,
  shouldFetchTikTokMetrics,
  shouldFetchReelMetrics,
  isSummaryActive,
  fetchCampaignMetrics: fetchCampaignMetricsAction,
  fetchPostMetrics: fetchPostMetricsAction,
  fetchStoryMetrics: fetchStoryMetricsAction,
}) => {
  const dispatch = useDispatch()

  useEffect(() => {
    if (isCampaignFetched && !isCampaignMetricsInErrorState) {
      fetchCampaignMetricsAction(campaignId)
    }

    if (shouldFetchPostMetrics) {
      fetchPostMetricsAction(campaignId, activeChannel)
    }

    if (shouldFetchStoryMetrics) {
      fetchStoryMetricsAction(campaignId, activeChannel)
    }

    if (shouldFetchTikTokMetrics) {
      dispatch(fetchTikTokMetrics(campaignId, activeChannel))
    }

    if (shouldFetchReelMetrics) {
      dispatch(fetchReelMetrics(campaignId))
    }
  }, [isCampaignFetched, activeChannel])

  if (showLoading) {
    return <Loading height={30} />
  }

  if (isSummaryActive) {
    return <AnalyticsPostCampaignReportSummary />
  }

  if (activeChannel === PCRChannels.IGPosts) {
    return <AnalyticsPostCampaignReportPost />
  }

  if (activeChannel === PCRChannels.IGStories) {
    return <AnalyticsPostCampaignReportStory />
  }

  if (activeChannel === PCRChannels.TikTok) {
    return <AnalyticsPostCampaignReportTikTok />
  }

  if (activeChannel === PCRChannels.Reel) {
    return <AnalyticsPostCampaignReportReel />
  }

  if (PCR_CHANNEL_SUMMARY_SUPPORTED_CHANNELS.includes(activeChannel)) {
    return (
      <PostCampaignReportChannelSummaryWrapper
        activeChannel={activeChannel}
        campaignId={campaignId}
      />
    )
  }

  return <PostCampaignReportPerformanceList />
}

const mapStateToProps = (state: IApplicationState, { match }: RouteComponentProps) => {
  const activeChannel = selectActivePCRChannel(state)
  const campaignId = selectCampaignId(match)!
  return {
    activeChannel,
    campaignId,
    isCampaignFetched: selectIsCampaignFetched(state, campaignId),
    showLoading: selectShowLoading(state),
    shouldFetchPostMetrics: selectShouldFetchPostMetrics(state, campaignId, activeChannel),
    shouldFetchStoryMetrics: selectShouldFetchStoryMetrics(state, campaignId, activeChannel),
    shouldFetchTikTokMetrics: selectShouldFetchTikTokMetrics(state, campaignId, activeChannel),
    shouldFetchReelMetrics: selectShouldFetchReelMetrics(state, campaignId, activeChannel),
    isCampaignMetricsInErrorState: selectIsCampaignMetricsInErrorState(state),
    isSummaryActive: selectIsSummaryActive(state),
  }
}

const mapDispatchToProps = {
  fetchCampaignMetrics,
  fetchPostMetrics,
  fetchStoryMetrics,
}

export { PostCampaignReportPerformanceListWrapper }
export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(PostCampaignReportPerformanceListWrapper)
